import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Heart, Warning, CheckCircle, Phone, User, Headset } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const DontUseAlonePage = () => {
  const navigate = useNavigate();
  const [checkInTime, setCheckInTime] = useState(30);
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [checkInEndTime, setCheckInEndTime] = useState(null);
  const [emergencyContact, setEmergencyContact] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    if (isActive && checkInEndTime) {
      const timer = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((checkInEndTime - now) / 1000 / 60));
        setTimeRemaining(remaining);

        if (remaining === 0) {
          handleCheckInExpired();
        } else if (remaining === 5) {
          toast.warning('⏰ 5 minutes until check-in reminder!');
        } else if (remaining === 1) {
          toast.warning('⚠️ 1 minute remaining - please check in!', {
            duration: 10000
          });
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isActive, checkInEndTime]);

  const startCheckIn = () => {
    const endTime = Date.now() + checkInTime * 60 * 1000;
    setCheckInEndTime(endTime);
    setTimeRemaining(checkInTime);
    setIsActive(true);
    toast.success(`✓ Check-in timer started for ${checkInTime} minutes`);
    
    if (emergencyContact && emergencyName) {
      toast.info(`Emergency contact ${emergencyName} will be notified if you don't check in`);
    }
  };

  const handleCheckIn = () => {
    setIsActive(false);
    setCheckInEndTime(null);
    setTimeRemaining(null);
    toast.success('✓ Checked in successfully! Stay safe.', {
      description: 'You can start a new timer anytime'
    });
  };

  const handleCheckInExpired = () => {
    setIsActive(false);
    
    // Show alert
    toast.error('⚠️ CHECK-IN TIME EXPIRED', {
      description: emergencyContact 
        ? `Attempting to notify ${emergencyName || 'emergency contact'}: ${emergencyContact}`
        : 'Please check in or contact someone for help',
      duration: 10000
    });
    
    // In a real app, this would send SMS/call to emergency contact
    if (emergencyContact) {
      console.log(`Would notify emergency contact: ${emergencyName} at ${emergencyContact}`);
      // Simulate notification
      setTimeout(() => {
        toast.info('📱 Emergency contact notification sent', {
          description: `Message sent to ${emergencyName}`,
          duration: 5000
        });
      }, 2000);
    }
  };

  const cancelCheckIn = () => {
    setIsActive(false);
    setCheckInEndTime(null);
    setTimeRemaining(null);
    toast.info('Timer cancelled');
  };

  const saveEmergencyContact = () => {
    if (emergencyContact && emergencyName) {
      toast.success(`Emergency contact saved: ${emergencyName}`);
      setShowContactForm(false);
    } else {
      toast.error('Please enter both name and contact number');
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      {/* Header */}
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <h1 className="text-2xl font-semibold tracking-tight text-[rgb(var(--text-primary))]">
              Don't Use Alone
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {/* NORS Canada - Virtual Safe Consumption */}
        <div className="mb-6 p-6 bg-gradient-to-br from-[rgb(var(--primary))]/20 to-[rgb(var(--accent))]/20 border border-[rgb(var(--primary))]/30 rounded-2xl">
          <div className="flex items-start mb-4">
            <div className="w-12 h-12 rounded-full bg-[rgb(var(--primary))]/20 flex items-center justify-center mr-4 flex-shrink-0">
              <Headset size={24} weight="duotone" className="text-[rgb(var(--primary))]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-2 mb-2">
                <h2 className="text-lg font-bold text-[rgb(var(--text-primary))]">
                  NORS Canada
                </h2>
                <span className="px-2 py-0.5 text-xs font-medium bg-[rgb(var(--success))]/20 text-[rgb(var(--success))] rounded-full">
                  24/7
                </span>
              </div>
              <p className="text-[rgb(var(--text-secondary))] text-sm leading-relaxed mb-3">
                Virtual safe consumption service. A peer operator stays on the line while you use, 
                monitors for overdose, and can call 911 or your emergency contact if needed.
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="tel:1-888-688-6677"
                  data-testid="nors-call-button"
                  className="inline-flex items-center px-4 py-2 bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/90 text-white font-semibold rounded-full transition-all text-sm"
                >
                  <Phone size={16} weight="bold" className="mr-2" />
                  Call 1-888-688-NORS
                </a>
                <a
                  href="sms:1-888-688-6677"
                  data-testid="nors-text-button"
                  className="inline-flex items-center px-4 py-2 bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] text-[rgb(var(--text-primary))] font-semibold rounded-full border border-white/10 transition-all text-sm"
                >
                  Text NORS
                </a>
              </div>
              <p className="text-xs text-[rgb(var(--text-secondary))] mt-3">
                Anonymous & confidential • Run by peers with lived experience
              </p>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mb-8 p-6 bg-[rgb(var(--surface))] border border-white/5 rounded-2xl">
          <div className="flex items-start mb-3">
            <Heart size={24} weight="duotone" className="text-[rgb(var(--primary))] mr-3 mt-1" />
            <div>
              <h2 className="text-lg font-semibold text-[rgb(var(--text-primary))] mb-2">
                Safer Use Check-In Timer
              </h2>
              <p className="text-[rgb(var(--text-secondary))] text-sm leading-relaxed">
                Set a timer to check in. If you don't check in on time, you'll get reminders. 
                Add an emergency contact to be notified if you don't check in.
              </p>
            </div>
          </div>
        </div>

        {!isActive ? (
          <div className="space-y-6">
            {/* Emergency Contact */}
            <div className="bg-[rgb(var(--surface))] rounded-2xl p-6 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <User size={20} weight="duotone" className="text-[rgb(var(--primary))] mr-2" />
                  <h3 className="font-semibold text-[rgb(var(--text-primary))]">
                    Emergency Contact
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowContactForm(!showContactForm)}
                  data-testid="toggle-contact-form"
                  className="text-[rgb(var(--primary))] hover:text-[rgb(var(--accent))]"
                >
                  {emergencyContact ? 'Edit' : 'Add'}
                </Button>
              </div>
              
              {emergencyContact && !showContactForm ? (
                <div className="flex items-center text-sm text-[rgb(var(--text-secondary))]">
                  <CheckCircle size={16} weight="duotone" className="text-[rgb(var(--success))] mr-2" />
                  {emergencyName} - {emergencyContact}
                </div>
              ) : showContactForm ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Contact name (e.g., Alex)"
                    value={emergencyName}
                    onChange={(e) => setEmergencyName(e.target.value)}
                    data-testid="emergency-name-input"
                    className="w-full px-4 py-3 bg-[rgb(var(--background))] border border-white/5 rounded-xl text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-secondary))] focus:outline-none focus:border-[rgb(var(--primary))]/50"
                  />
                  <input
                    type="tel"
                    placeholder="Phone number"
                    value={emergencyContact}
                    onChange={(e) => setEmergencyContact(e.target.value)}
                    data-testid="emergency-contact-input"
                    className="w-full px-4 py-3 bg-[rgb(var(--background))] border border-white/5 rounded-xl text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-secondary))] focus:outline-none focus:border-[rgb(var(--primary))]/50"
                  />
                  <Button
                    onClick={saveEmergencyContact}
                    data-testid="save-contact-button"
                    className="w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/90 text-white"
                  >
                    Save Contact
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-[rgb(var(--text-secondary))]">
                  Optional: Add someone to notify if you don't check in
                </p>
              )}
            </div>

            {/* Timer Selection */}
            <div>
              <label className="block text-sm font-medium text-[rgb(var(--text-secondary))] mb-3">
                How long until check-in? (minutes)
              </label>
              <div className="grid grid-cols-4 gap-3">
                {[15, 30, 45, 60].map((time) => (
                  <button
                    key={time}
                    onClick={() => setCheckInTime(time)}
                    data-testid={`time-${time}`}
                    className={`p-4 rounded-xl border transition-all ${
                      checkInTime === time
                        ? 'bg-[rgb(var(--primary))]/20 border-[rgb(var(--primary))] text-[rgb(var(--text-primary))]'
                        : 'bg-[rgb(var(--surface))] border-white/5 text-[rgb(var(--text-secondary))] hover:border-[rgb(var(--primary))]/20'
                    }`}
                  >
                    <div className="text-2xl font-semibold">{time}</div>
                    <div className="text-xs">min</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Start Button */}
            <Button
              onClick={startCheckIn}
              data-testid="start-check-in"
              className="w-full min-h-[64px] rounded-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/90 text-white font-semibold text-lg shadow-lg shadow-[rgb(var(--primary))]/20 btn-hover"
            >
              <Clock size={24} weight="duotone" className="mr-2" />
              Start Check-In Timer
            </Button>

            {/* Safety Tips */}
            <div className="mt-8 space-y-3">
              <h3 className="text-sm font-medium text-[rgb(var(--text-secondary))] uppercase tracking-wider">
                Safety Tips
              </h3>
              <div className="space-y-3">
                {[
                  'Let a trusted friend know what you\'re doing',
                  'Have naloxone/Narcan nearby and know how to use it',
                  'Start with a small amount first',
                  'Keep water and snacks close by',
                  'Have emergency numbers saved in your phone'
                ].map((tip, index) => (
                  <div
                    key={index}
                    className="flex items-start p-4 bg-[rgb(var(--surface))] rounded-xl border border-white/5"
                  >
                    <CheckCircle size={20} weight="duotone" className="text-[rgb(var(--success))] mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">
                      {tip}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active Timer */}
            <div className="text-center py-12">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-[rgb(var(--primary))] to-[rgb(var(--accent))] mb-4">
                  <Clock size={64} weight="duotone" className="text-white" />
                </div>
              </div>
              <div className="mb-4">
                <div className="text-6xl font-bold text-[rgb(var(--primary))] mb-2">
                  {timeRemaining}
                </div>
                <div className="text-[rgb(var(--text-secondary))]">
                  minutes remaining
                </div>
              </div>
              <p className="text-[rgb(var(--text-secondary))] max-w-md mx-auto">
                Check in when this timer ends to let us know you're okay
              </p>
              {emergencyContact && (
                <p className="text-sm text-[rgb(var(--primary))] mt-3">
                  {emergencyName} will be notified if you don't check in
                </p>
              )}
            </div>

            {/* Check In Button */}
            <Button
              onClick={handleCheckIn}
              data-testid="check-in-button"
              className="w-full min-h-[64px] rounded-full bg-[rgb(var(--success))] hover:bg-[rgb(var(--success))]/90 text-white font-semibold text-lg shadow-lg btn-hover"
            >
              <CheckCircle size={24} weight="duotone" className="mr-2" />
              I'm Okay - Check In Now
            </Button>

            <Button
              onClick={cancelCheckIn}
              data-testid="cancel-check-in"
              variant="outline"
              className="w-full min-h-[56px] rounded-full border-[rgb(var(--text-secondary))]/30 text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--surface))]"
            >
              Cancel Timer
            </Button>

            {/* Warning */}
            <div className="mt-8 p-6 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-2xl">
              <div className="flex items-start">
                <Warning size={24} weight="duotone" className="text-[rgb(var(--danger))] mr-3 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-[rgb(var(--danger))] mb-2">
                    If You Need Help
                  </h3>
                  <p className="text-[rgb(var(--text-secondary))] text-sm leading-relaxed mb-3">
                    If you're experiencing an overdose or medical emergency:
                  </p>
                  <a
                    href="tel:911"
                    data-testid="emergency-call-911"
                    className="inline-flex items-center px-6 py-3 bg-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/90 text-white font-semibold rounded-full transition-all"
                  >
                    <Phone size={20} weight="bold" className="mr-2" />
                    Call 911 Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DontUseAlonePage;